import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory, faPlay, faSpinner, faSync, faCheck,
  faTimes, faArrowUp, faArrowDown, faMinus, faPlus
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

const COMPARISON_CONFIG = {
  regression: { icon: faArrowDown, color: 'text-red-400', bg: 'bg-red-900/20', label: 'Regression' },
  improvement: { icon: faArrowUp, color: 'text-green-400', bg: 'bg-green-900/20', label: 'Improvement' },
  unchanged: { icon: faMinus, color: 'text-gray-400', bg: '', label: 'Unchanged' },
  changed: { icon: faSync, color: 'text-yellow-400', bg: 'bg-yellow-900/10', label: 'Changed' },
  error: { icon: faTimes, color: 'text-red-500', bg: 'bg-red-900/10', label: 'Error' }
};

const RegressionTestPanel = ({ webTarget, isDarkMode }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(null);
  const [showCreateFrom, setShowCreateFrom] = useState(false);
  const [authzScenarios, setAuthzScenarios] = useState([]);

  useEffect(() => {
    if (webTarget?._id) {
      loadTests();
      loadAuthzScenarios();
    }
  }, [webTarget]);

  const loadTests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/web-reports/regression/${webTarget._id}`);
      setTests(res.data || []);
    } catch (err) {
      console.error('Failed to load regression tests:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAuthzScenarios = async () => {
    try {
      const res = await axios.get(`/authz-test/scenarios/${webTarget._id}`);
      setAuthzScenarios((res.data || []).filter(s => s.status === 'completed'));
    } catch {}
  };

  const handleCreate = async (scenarioId) => {
    try {
      await axios.post('/web-reports/regression/create', { scenarioId });
      setShowCreateFrom(false);
      loadTests();
    } catch (err) {
      console.error('Failed to create regression test:', err);
    }
  };

  const handleRun = async (testId) => {
    setRunning(testId);
    try {
      await axios.post(`/web-reports/regression/run/${testId}`);
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`/web-reports/regression/status/${testId}`);
          if (!res.data.isRunning) {
            clearInterval(poll);
            setRunning(null);
            loadTests();
            if (selectedTest === testId) loadResults(testId);
          }
        } catch {
          clearInterval(poll);
          setRunning(null);
        }
      }, 2000);
    } catch (err) {
      setRunning(null);
      console.error('Failed to run regression test:', err);
    }
  };

  const loadResults = async (testId) => {
    setSelectedTest(testId);
    try {
      const res = await axios.get(`/web-reports/regression/results/${testId}`);
      setResults(res.data);
    } catch {
      setResults(null);
    }
  };

  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const text = isDarkMode ? 'text-gray-300' : 'text-gray-700';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-3 py-2 border-b ${border} flex items-center justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faHistory} className="text-orange-400" />
          <span className={`text-xs font-medium ${text}`}>Regression Tests</span>
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({tests.length})</span>
        </div>
        <div className="flex gap-2">
          <button onClick={loadTests} className={`p-1 text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button onClick={() => setShowCreateFrom(!showCreateFrom)}
            className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 flex items-center gap-1">
            <FontAwesomeIcon icon={faPlus} /> Create from AuthZ Test
          </button>
        </div>
      </div>

      {/* Create from scenario selector */}
      {showCreateFrom && (
        <div className={`px-3 py-2 border-b ${border} ${isDarkMode ? 'bg-gray-800/50' : 'bg-orange-50/50'}`}>
          <div className={`text-[10px] font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Select a completed AuthZ scenario to create a regression baseline:
          </div>
          <div className="space-y-1">
            {authzScenarios.length === 0 ? (
              <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No completed AuthZ test scenarios available.</div>
            ) : authzScenarios.map(s => (
              <button key={s._id} onClick={() => handleCreate(s._id)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                <span>{s.name}</span>
                <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(s.createdAt).toLocaleDateString()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Test list */}
        <div className={`w-64 border-r ${border} overflow-y-auto`}>
          {tests.map(test => {
            const summary = test.regressionConfig?.lastRunSummary;
            const hasRegressions = summary?.regressions > 0;
            return (
              <div key={test._id}
                onClick={() => loadResults(test._id)}
                className={`px-3 py-2.5 cursor-pointer border-b ${border} ${
                  selectedTest === test._id
                    ? (isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50')
                    : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50')
                }`}>
                <div className={`text-xs font-medium truncate ${text}`}>{test.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  {summary ? (
                    <>
                      {hasRegressions && <span className="text-[10px] text-red-400">{summary.regressions} regr.</span>}
                      {summary.improvements > 0 && <span className="text-[10px] text-green-400">{summary.improvements} impr.</span>}
                      <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{summary.unchanged} same</span>
                    </>
                  ) : (
                    <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Not yet run</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={(e) => { e.stopPropagation(); handleRun(test._id); }}
                    disabled={running === test._id}
                    className="text-[10px] text-orange-400 hover:text-orange-300 flex items-center gap-1">
                    {running === test._id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />} Run
                  </button>
                  {test.regressionConfig?.lastRunAt && (
                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      Last: {new Date(test.regressionConfig.lastRunAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {tests.length === 0 && !loading && (
            <div className={`p-4 text-xs text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              No regression tests. Create one from a completed AuthZ test.
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {results ? (
            <>
              {/* Summary bar */}
              {results.summary && (
                <div className={`px-3 py-2 border-b ${border} flex items-center gap-4 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50/50'}`}>
                  <span className={`text-xs font-medium ${text}`}>{results.name}</span>
                  {results.summary.regressions > 0 && <span className="text-xs text-red-400 font-bold">{results.summary.regressions} Regressions</span>}
                  {results.summary.improvements > 0 && <span className="text-xs text-green-400">{results.summary.improvements} Improvements</span>}
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{results.summary.unchanged} Unchanged</span>
                </div>
              )}

              {/* Results table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                      <th className="px-3 py-1.5 text-left font-medium">Method</th>
                      <th className="px-3 py-1.5 text-left font-medium">URL</th>
                      <th className="px-3 py-1.5 text-left font-medium">Previous</th>
                      <th className="px-3 py-1.5 text-left font-medium">Current</th>
                      <th className="px-3 py-1.5 text-left font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(results.results || []).map((r, i) => {
                      const cc = COMPARISON_CONFIG[r.comparison] || COMPARISON_CONFIG.unchanged;
                      return (
                        <tr key={i} className={`border-b ${border} ${cc.bg}`}>
                          <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{r.method}</td>
                          <td className={`px-3 py-2 font-mono truncate max-w-md ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{r.url}</td>
                          <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{r.previousStatusCode || '-'}</td>
                          <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.currentStatusCode || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`flex items-center gap-1 ${cc.color}`}>
                              <FontAwesomeIcon icon={cc.icon} className="text-[10px]" /> {cc.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Select a regression test to view results.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegressionTestPanel;
